import React, { createContext, useContext } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Button } from "@shadcn/components/ui/button";
import { Form } from "@shadcn/components/ui/form";
import { FormStepProps } from "./FormStep.types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

const FormStepValidatingContext = createContext<(v: boolean) => void>(() => {});
export const useFormStepValidating = () => useContext(FormStepValidatingContext);

export type PreSubmissionCallback = () => Promise<boolean> | boolean;
const FormStepPreSubmitContext = createContext<
  (fn: PreSubmissionCallback) => () => void
>(() => () => {});
export const useFormStepPreSubmission = () => useContext(FormStepPreSubmitContext);
import * as Schema from "@schema-types/types/JourneySchema";
import {
  ElementSchema,
  FormStepSchema,
  QuestionElementSchema,
  IntegrationElementSchema,
  Pipe,
  BranchJunction,
  BranchRule,
  ElementType,
  JunctionType,
  FieldType,
  StepType,
} from "@schema-types/types/JourneySchema";
import { findIntegrationBranchProperty } from "@schema-types/integrationBranchProperties";
import { evaluateRule } from "./evaluateRule";
import { Repeater } from "@forms/form-render/components/Elements/Repeater";
import { ElementRenderer } from "@forms/form-render/components/Elements/ElementRenderer";
import { FormFileUploadEnquiryProvider } from "@forms/form-render/contexts/FormFileUploadEnquiryContext";
import { EnquiryEvaluationTimeZoneProvider } from "@forms/form-render/contexts/EnquiryEvaluationTimeZoneContext";
import { TurnstileActionFeedback } from "../../_common/TurnstileActionFeedback";
import { getBrowserIanaTimeZoneId } from "@schema-types/enquiryEvaluationTimeZone";
import { cn } from "@shadcn/lib/utils";

type ResolvedBranchSource = {
  stepId: string;
  dataKey: string;
  propertyPath?: string;
};

const resolveBranchSourceInElements = (
  elements: ElementSchema[],
  sourceElementId: string,
  sourceFieldId: string
): Omit<ResolvedBranchSource, "stepId"> | undefined => {
  const element = elements.find((e) => e.id === sourceElementId);
  if (!element) return undefined;

  if (element.type === ElementType.Question) {
    const question = element as QuestionElementSchema;
    const field = question.fields.find((f) => f.id === sourceFieldId);
    if (field && field.type === FieldType.Input) {
      return { dataKey: field.name };
    }
    return undefined;
  }

  if (element.type === ElementType.Integration) {
    const integration = element as IntegrationElementSchema;
    const property = findIntegrationBranchProperty(integration.integrationType, sourceFieldId);
    if (!property) return undefined;
    return { dataKey: integration.displayId, propertyPath: property.path };
  }

  return undefined;
};

const resolveBranchSourceInJourney = (
  journey: Schema.JourneySchema | undefined,
  sourceElementId: string,
  sourceFieldId: string
): ResolvedBranchSource | undefined => {
  if (!journey?.steps) return undefined;
  for (const step of journey.steps) {
    if (step.type !== StepType.Form) continue;
    const formStep = step as FormStepSchema;
    if (!formStep.elements) continue;
    const resolved = resolveBranchSourceInElements(formStep.elements, sourceElementId, sourceFieldId);
    if (resolved) return { stepId: step.id, ...resolved };
  }
  return undefined;
};

const readResolvedValue = (
  data: Record<string, any> | undefined,
  resolved: { dataKey: string; propertyPath?: string }
): unknown => {
  if (!data) return undefined;
  const container = data[resolved.dataKey];
  if (!resolved.propertyPath) return container;
  if (container == null || typeof container !== "object") return undefined;
  return (container as Record<string, unknown>)[resolved.propertyPath];
};

const evaluateRuleInForm = (
  rule: BranchRule,
  formValues: Record<string, any>,
  elements: ElementSchema[],
  journey: Schema.JourneySchema | undefined,
  journeyData: Array<{ stepId: string; data: Record<string, any> }> | undefined,
  currentFormId: string | undefined,
  evaluationTimeZoneId: string
): boolean => {
  const { sourceQuestionId, sourceFieldId } = rule;

  if (journey && journeyData) {
    const found = resolveBranchSourceInJourney(journey, sourceQuestionId, sourceFieldId);
    if (found && found.stepId !== currentFormId) {
      const stepData = journeyData.find((s) => s.stepId === found.stepId)?.data;
      const value = readResolvedValue(stepData, found);
      return evaluateRule(
        value,
        rule.comparisonValue,
        rule.comparisonType,
        rule.comparisonValueType,
        evaluationTimeZoneId
      );
    }
  }

  const resolved = resolveBranchSourceInElements(elements, sourceQuestionId, sourceFieldId);
  if (!resolved) return false;

  const value = readResolvedValue(formValues, resolved);
  return evaluateRule(value, rule.comparisonValue, rule.comparisonType, rule.comparisonValueType, evaluationTimeZoneId);
};

const renderElement = (
  formStepId: string,
  element: ElementSchema,
  isSubmitting: boolean,
  enquiryId?: string
) => {
  return (
    <ElementRenderer
      key={element.id}
      element={element}
      formStepId={formStepId}
      disabled={isSubmitting}
      enquiryId={enquiryId}
      currentStepId={formStepId}
    />
  );
};

const getNextPipeId = (
  currentPipe: Pipe,
  formValues: Record<string, any>,
  elements: ElementSchema[],
  journey: Schema.JourneySchema | undefined,
  journeyData: Array<{ stepId: string; data: Record<string, any> }> | undefined,
  currentFormId: string | undefined,
  evaluationTimeZoneId: string
): string | undefined => {
  if (!currentPipe.junctions || currentPipe.junctions.length === 0) {
    return undefined;
  }

  const evaluate = (rule: BranchRule) =>
    evaluateRuleInForm(rule, formValues, elements, journey, journeyData, currentFormId, evaluationTimeZoneId);

  const branchJunctions = currentPipe.junctions.filter((link) => link.type === JunctionType.Branch) as BranchJunction[];
  for (const branch of branchJunctions) {
    if (branch.rules) {
      const matchingRule = branch.rules.find((rule) => evaluate(rule));
      if (matchingRule) {
        return branch.targetPipeId;
      }
    }
  }

  const directLink = currentPipe.junctions.find((link) => link.type === JunctionType.Direct);
  if (directLink) {
    return directLink.targetPipeId;
  }

  return undefined;
};

const traverseElements = (
  formStepId: string,
  elements: ElementSchema[],
  formPipeline: Pipe[],
  isSubmitting: boolean,
  formValues: Record<string, any>,
  enquiryId: string | undefined,
  journey: Schema.JourneySchema | undefined,
  journeyData: Array<{ stepId: string; data: Record<string, any> }> | undefined,
  evaluationTimeZoneId: string
): React.ReactElement[] => {
  const renderedElements: React.ReactElement[] = [];
  if (!formPipeline || formPipeline.length === 0) return [];

  let currentPipe = formPipeline[0];
  if (currentPipe.type === Schema.PipeType.Start) {
    const nextPipeId = getNextPipeId(
      currentPipe,
      formValues,
      elements,
      journey,
      journeyData,
      formStepId,
      evaluationTimeZoneId
    );
    currentPipe = nextPipeId ? formPipeline.find((p) => p.id === nextPipeId) : undefined;
  }

  while (currentPipe) {
    if (currentPipe.type === Schema.PipeType.Start) {
      const nextPipeId = getNextPipeId(
        currentPipe,
        formValues,
        elements,
        journey,
        journeyData,
        formStepId,
        evaluationTimeZoneId
      );
      currentPipe = nextPipeId ? formPipeline.find((p) => p.id === nextPipeId) : undefined;
      continue;
    }
    if (currentPipe.type === Schema.PipeType.ElementRepeater) {
      const repeaterPipe = currentPipe as Schema.ElementRepeaterPipe;
      const nestedPipeline = repeaterPipe.pipeline ?? [];

      renderedElements.push(
        <Repeater
          key={repeaterPipe.id}
          id={repeaterPipe.id}
          displayId={repeaterPipe.displayId || repeaterPipe.id}
          title={repeaterPipe.title}
          elements={elements}
          pipeline={nestedPipeline}
          minItems={repeaterPipe.minItems}
          maxItems={repeaterPipe.maxItems}
          addButtonText={repeaterPipe.addButtonText}
          removeButtonText={repeaterPipe.removeButtonText}
          hideElementTitles={repeaterPipe.hideElementTitles}
          disabled={isSubmitting}
          formStepId={formStepId}
          journey={journey}
          journeyData={journeyData}
          evaluationTimeZoneId={evaluationTimeZoneId}
        />
      );
    } else {
      const standardPipe = currentPipe as Schema.StandardPipe;
      const element = elements.find((e) => e.id === standardPipe.targetId);
      if (element) {
        renderedElements.push(renderElement(formStepId, element, isSubmitting, enquiryId));
      }
    }

    const nextPipe = getNextPipeId(
      currentPipe,
      formValues,
      elements,
      journey,
      journeyData,
      formStepId,
      evaluationTimeZoneId
    );
    if (!nextPipe) break;

    currentPipe = formPipeline.find((item) => item.id === nextPipe);
  }

  return renderedElements;
};

const FormStepContent = ({
  id,
  elements,
  formPipeline: formPipeline,
  data,
  journey,
  journeyData,
  onSubmit,
  onBack,
  hasExistingData,
  enquiryId,
  renderBeforeActions,
  disableNextUntilTurnstile,
  turnstileFeedback,
  staffEnquiryPreviewMode,
  staffEnquiryPreviewSaveDisabled,
  readOnly,
  evaluationTimeZoneId,
}: FormStepProps) => {
  const evaluationTz = evaluationTimeZoneId?.trim() || getBrowserIanaTimeZoneId();
  const form = useForm({
    defaultValues: data,
    shouldUseNativeValidation: false,
    shouldUnregister: true,
    mode: "onBlur",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const preSubmitCallbacksRef = useRef<PreSubmissionCallback[]>([]);
  const formValues = form.watch();
  const previewSaveBlocked = Boolean(staffEnquiryPreviewMode && staffEnquiryPreviewSaveDisabled);
  const isTurnstileWaiting = !!disableNextUntilTurnstile;
  const isActionInFlight = isSubmitting || isValidating || previewSaveBlocked;
  const isButtonsDisabled = isActionInFlight || isTurnstileWaiting;
  const fieldsDisabled = readOnly || isButtonsDisabled;

  const registerPreSubmissionCall = useCallback((fn: PreSubmissionCallback) => {
    preSubmitCallbacksRef.current.push(fn);
    return () => {
      preSubmitCallbacksRef.current = preSubmitCallbacksRef.current.filter((f) => f !== fn);
    };
  }, []);

  const lastResetDataRef = useRef<typeof data | symbol>(Symbol("initial"));
  useEffect(() => {
    if (isSubmitting) return;
    if (lastResetDataRef.current !== data) {
      lastResetDataRef.current = data;
      form.reset(data);
    }
  }, [data, isSubmitting, form]);

  useEffect(() => {
    form.reset();
  }, [id]);

  const handleSubmit: SubmitHandler<any> = async (submitData) => {
    if (readOnly) {
      return;
    }
    setIsSubmitting(true);
    setIsValidating(true);
    try {
      const callbacks = [...preSubmitCallbacksRef.current];
      const results = await Promise.all(callbacks.map((cb) => Promise.resolve(cb())));
      if (results.some((r) => r === false)) return;
      await onSubmit(id, submitData);
    } finally {
      setIsValidating(false);
      setIsSubmitting(false);
    }
  };

  return (
    <EnquiryEvaluationTimeZoneProvider timeZoneId={evaluationTimeZoneId ?? null}>
    <FormStepValidatingContext.Provider value={setIsValidating}>
      <FormStepPreSubmitContext.Provider value={registerPreSubmissionCall}>
        <Form {...form}>
          <FormFileUploadEnquiryProvider enquiryId={enquiryId}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className={cn("space-y-10 sm:space-y-12", readOnly && "read-only-mode")}
            noValidate
          >
          {traverseElements(id, elements, formPipeline, fieldsDisabled, formValues, enquiryId, journey, journeyData, evaluationTz)}

          {!readOnly && renderBeforeActions && (
            <div className="flex justify-center my-4">
              {renderBeforeActions}
            </div>
          )}

          {!readOnly ? (
            <div className="mt-8 sm:mt-4">
              <div className="-mx-6 border-t border-gray-200 px-6 py-4 sm:mx-0 sm:border-none sm:px-0 sm:py-0">
                <div className="mx-auto w-full max-w-3xl">
                  {turnstileFeedback ? (
                    <TurnstileActionFeedback className="mb-4" {...turnstileFeedback} />
                  ) : null}
                  <div
                    className={`mx-auto flex w-full max-w-3xl flex-row items-center gap-3 ${
                      staffEnquiryPreviewMode ? "justify-end" : "justify-between"
                    }`}
                  >
                    {!staffEnquiryPreviewMode && (
                      <Button
                        type="button"
                        onClick={onBack}
                        variant="default"
                        disabled={isButtonsDisabled}
                        size="lg"
                        className="flex-1 sm:flex-none"
                      >
                        {isActionInFlight ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Please wait
                          </>
                        ) : (
                          "Back"
                        )}
                      </Button>
                    )}
                    <Button type="submit" variant="default" disabled={isButtonsDisabled} size="lg" className="flex-1 sm:flex-none">
                      {isActionInFlight ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Please wait
                        </>
                      ) : staffEnquiryPreviewMode ? (
                        "Save"
                      ) : hasExistingData ? (
                        "Update"
                      ) : (
                        "Next"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          </form>
          </FormFileUploadEnquiryProvider>
        </Form>
      </FormStepPreSubmitContext.Provider>
    </FormStepValidatingContext.Provider>
    </EnquiryEvaluationTimeZoneProvider>
  );
};

export const FormStep = (props: FormStepProps) => {
  return <FormStepContent {...props} />;
};
